import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import './style.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../ui/Button';
import CloseIcon from '../../assets/icons/close.svg';
import CameraSwitchIcon from '../../assets/icons/camera_switch.svg';
const buildVideoConstraints = (facingMode) => ({
    audio: false,
    video: {
        facingMode: { ideal: facingMode },
        width: { ideal: 1920 },
        height: { ideal: 1440 },
    },
});
const InPageCamera = ({ open, onCapture, onClose }) => {
    const { t } = useTranslation();
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const [state, setState] = useState('idle');
    const [errorMessage, setErrorMessage] = useState(null);
    const [captured, setCaptured] = useState(null);
    const [facingMode, setFacingMode] = useState('environment');
    const stopStream = useCallback(() => {
        const stream = streamRef.current;
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    }, []);
    const startStream = useCallback(async (mode) => {
        if (!navigator.mediaDevices?.getUserMedia) {
            setState('error');
            setErrorMessage(t('camera.unavailable'));
            return;
        }
        setState('requesting');
        setErrorMessage(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia(buildVideoConstraints(mode));
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                // Play is required on some Android browsers even with autoPlay.
                await videoRef.current.play().catch(() => undefined);
            }
            setState('streaming');
        }
        catch (err) {
            const name = err?.name;
            stopStream();
            setState('error');
            if (name === 'NotAllowedError' || name === 'SecurityError') {
                setErrorMessage(t('camera.permission_denied'));
            }
            else {
                setErrorMessage(t('camera.unavailable'));
            }
        }
    }, [stopStream, t]);
    // Acquire / release the camera based on the `open` prop. Each open resets to
    // the rear camera so the user gets a predictable starting point.
    useEffect(() => {
        if (open) {
            setFacingMode('environment');
            void startStream('environment');
        }
        return () => {
            stopStream();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const handleSwitchCamera = useCallback(async () => {
        if (state !== 'streaming')
            return;
        const next = facingMode === 'environment' ? 'user' : 'environment';
        stopStream();
        setFacingMode(next);
        await startStream(next);
    }, [facingMode, state, startStream, stopStream]);
    // Revoke the captured preview URL when it changes or unmounts.
    useEffect(() => {
        return () => {
            if (captured?.previewUrl) {
                URL.revokeObjectURL(captured.previewUrl);
            }
        };
    }, [captured]);
    const handleCapture = useCallback(() => {
        const video = videoRef.current;
        if (!video || state !== 'streaming')
            return;
        const width = video.videoWidth;
        const height = video.videoHeight;
        if (!width || !height)
            return;
        // Allocate the canvas just for this capture so the bitmap is freed as soon
        // as toBlob completes and the canvas falls out of scope.
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        ctx.drawImage(video, 0, 0, width, height);
        canvas.toBlob((blob) => {
            if (!blob)
                return;
            const previewUrl = URL.createObjectURL(blob);
            setCaptured((prev) => {
                if (prev?.previewUrl)
                    URL.revokeObjectURL(prev.previewUrl);
                return { blob, previewUrl };
            });
            setState('captured');
        }, 'image/jpeg', 0.92);
    }, [state]);
    const handleRetake = useCallback(() => {
        setCaptured((prev) => {
            if (prev?.previewUrl)
                URL.revokeObjectURL(prev.previewUrl);
            return null;
        });
        setState('streaming');
    }, []);
    const handleUse = useCallback(() => {
        if (!captured)
            return;
        const file = new File([captured.blob], `photo-${Date.now()}.jpg`, {
            type: captured.blob.type || 'image/jpeg',
        });
        URL.revokeObjectURL(captured.previewUrl);
        setCaptured(null);
        stopStream();
        setState('idle');
        onCapture(file);
    }, [captured, onCapture, stopStream]);
    const handleClose = useCallback(() => {
        if (captured?.previewUrl) {
            URL.revokeObjectURL(captured.previewUrl);
        }
        setCaptured(null);
        stopStream();
        setState('idle');
        onClose();
    }, [captured, onClose, stopStream]);
    if (!open)
        return null;
    return (_jsxs("div", { className: "in-page-camera", role: "dialog", "aria-modal": "true", "aria-label": t('camera.title'), children: [_jsx("button", { type: "button", className: "in-page-camera-close", onClick: handleClose, "aria-label": t('button.cancel'), children: _jsx(CloseIcon, {}) }), _jsxs("div", { className: "in-page-camera-stage", children: [state === 'requesting' && (_jsx("div", { className: "in-page-camera-message", children: t('camera.starting') })), state === 'error' && (_jsx("div", { className: "in-page-camera-message in-page-camera-message--error", children: errorMessage })), _jsx("video", { ref: videoRef, className: 'in-page-camera-video' +
                            (facingMode === 'user' && state === 'streaming' ? ' in-page-camera-video--mirrored' : ''), autoPlay: true, playsInline: true, muted: true, hidden: state !== 'streaming' }), state === 'captured' && captured && (_jsx("img", { src: captured.previewUrl, alt: t('camera.preview_alt'), className: "in-page-camera-still" }))] }), _jsxs("div", { className: "in-page-camera-controls", children: [state === 'streaming' && (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: "in-page-camera-shutter", onClick: handleCapture, "aria-label": t('button.capture'), children: _jsx("span", { className: "in-page-camera-shutter-inner" }) }), _jsx("button", { type: "button", className: "in-page-camera-switch", onClick: handleSwitchCamera, "aria-label": t('button.switch_camera'), title: t('button.switch_camera'), children: _jsx(CameraSwitchIcon, { "aria-hidden": "true" }) })] })), state === 'captured' && (_jsxs(_Fragment, { children: [_jsx(Button, { label: t('button.retake'), variant: "outlined", onClick: handleRetake }), _jsx(Button, { label: t('button.use_photo'), onClick: handleUse })] })), state === 'error' && (_jsx(Button, { label: t('button.cancel'), variant: "outlined", onClick: handleClose }))] })] }));
};
export default InPageCamera;
